import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';

interface KnowledgeEntry {
  id: string;
  question: string;
  answer: string;
  category: string;
  status: string;
  tags: string[];
  isFaq: boolean;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

interface TopQuery {
  queryText: string;
  count: number;
}

interface ContentGap {
  queryText: string;
  count: number;
}

interface DailyTrend {
  date: string;
  count: number;
}

const CATEGORIES = ['Events', 'Registration', 'Logistics', 'Cultural', 'Accommodations', 'Subcommittees', 'General'];
const STATUSES = ['Draft', 'PendingReview', 'Published', 'Archived'];

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '1rem 1.25rem' },
  sectionTitle: { fontSize: '1rem', fontWeight: 600, color: '#1a6b3c', margin: '0 0 0.75rem 0' },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' as const },
  tab: {
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  tabActive: {
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    border: '1px solid #1a6b3c',
    backgroundColor: '#e8f5e9',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
    color: '#1a6b3c',
  },
  table: { width: '100%', borderCollapse: 'collapse' as const, marginBottom: '1rem' },
  th: {
    textAlign: 'left' as const,
    padding: '0.5rem 0.6rem',
    borderBottom: '2px solid #e0e0e0',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase' as const,
  },
  td: {
    padding: '0.5rem 0.6rem',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.8rem',
    color: '#333',
  },
  btn: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnPrimary: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontWeight: 500,
    backgroundColor: '#1a6b3c',
    color: '#fff',
  },
  btnDanger: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontWeight: 500,
    backgroundColor: '#d32f2f',
    color: '#fff',
  },
  btnSuccess: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontWeight: 500,
    backgroundColor: '#2e7d32',
    color: '#fff',
  },
  btnWarning: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontWeight: 500,
    backgroundColor: '#f57c00',
    color: '#fff',
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    overflowY: 'auto' as const,
  },
  formGroup: { marginBottom: '0.75rem' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.25rem', color: '#333' },
  input: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.85rem',
    minHeight: '100px',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
  },
  statusBadge: {
    display: 'inline-block',
    padding: '0.15rem 0.4rem',
    borderRadius: '10px',
    fontSize: '0.7rem',
    fontWeight: 500,
  },
  sectionDivider: {
    borderTop: '1px solid #e0e0e0',
    marginTop: '1.5rem',
    paddingTop: '1rem',
  },
  barChart: { display: 'flex', alignItems: 'flex-end', gap: '2px', height: '80px', marginTop: '0.5rem' },
  bar: {
    flex: 1,
    backgroundColor: '#1a6b3c',
    borderRadius: '2px 2px 0 0',
    minWidth: '8px',
    maxWidth: '20px',
  },
  empty: { textAlign: 'center' as const, padding: '1rem', color: '#999', fontSize: '0.85rem' },
  actions: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' as const },
};

const statusColors: Record<string, string> = {
  Draft: '#9e9e9e',
  PendingReview: '#f57c00',
  Published: '#2e7d32',
  Archived: '#616161',
};

type PanelTab = 'entries' | 'review' | 'analytics';

export function AdminKnowledgePanel() {
  const [activeTab, setActiveTab] = useState<PanelTab>('entries');
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [formData, setFormData] = useState({ question: '', answer: '', category: 'General', tags: '', status: 'Draft' });
  const [reviewQueue, setReviewQueue] = useState<KnowledgeEntry[]>([]);
  const [topQueries, setTopQueries] = useState<TopQuery[]>([]);
  const [contentGaps, setContentGaps] = useState<ContentGap[]>([]);
  const [dailyTrends, setDailyTrends] = useState<DailyTrend[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await apiClient<{ entries: KnowledgeEntry[]; total: number }>(`/knowledge/entries${params}`);
      setEntries(res.entries || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchReviewQueue = useCallback(async () => {
    try {
      const res = await apiClient<{ entries: KnowledgeEntry[]; total: number }>('/knowledge/review-queue');
      setReviewQueue(res.entries || []);
    } catch {
      setReviewQueue([]);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [queries, gaps, trends] = await Promise.all([
        apiClient<TopQuery[]>('/knowledge/analytics/top-queries'),
        apiClient<ContentGap[]>('/knowledge/analytics/gaps'),
        apiClient<DailyTrend[]>('/knowledge/analytics/trends'),
      ]);
      setTopQueries(queries || []);
      setContentGaps(gaps || []);
      setDailyTrends(trends || []);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'entries') fetchEntries();
    else if (activeTab === 'review') fetchReviewQueue();
    else if (activeTab === 'analytics') fetchAnalytics();
  }, [activeTab, fetchEntries, fetchReviewQueue, fetchAnalytics]);

  const handleCreate = () => {
    setEditingEntry(null);
    setFormData({ question: '', answer: '', category: 'General', tags: '', status: 'Draft' });
    setShowModal(true);
  };

  const handleEdit = (entry: KnowledgeEntry) => {
    setEditingEntry(entry);
    setFormData({
      question: entry.question,
      answer: entry.answer,
      category: entry.category,
      tags: entry.tags.join(', '),
      status: entry.status,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const body: any = {
      question: formData.question,
      answer: formData.answer,
      category: formData.category,
      tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
      status: formData.status,
    };

    try {
      if (editingEntry) {
        await apiClient(`/knowledge/entries/${editingEntry.id}`, { method: 'PATCH', body });
        showToast('Entry updated successfully');
      } else {
        await apiClient('/knowledge/entries', { method: 'POST', body });
        showToast('Entry created successfully');
      }
      setShowModal(false);
      fetchEntries();
    } catch {
      showToast('Error saving entry');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient(`/knowledge/entries/${id}`, { method: 'DELETE' });
      showToast('Entry deleted');
      setDeleteConfirm(null);
      fetchEntries();
    } catch {
      showToast('Error deleting entry');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await apiClient(`/knowledge/entries/${id}/approve`, { method: 'PATCH' });
      showToast('Entry approved and published');
      fetchReviewQueue();
    } catch {
      showToast('Error approving entry');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await apiClient(`/knowledge/entries/${id}/reject`, { method: 'PATCH' });
      showToast('Entry rejected');
      fetchReviewQueue();
    } catch {
      showToast('Error rejecting entry');
    }
  };

  const maxTrendCount = Math.max(...dailyTrends.map((t) => t.count), 1);

  return (
    <div style={styles.container}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem', padding: '0.75rem 1.25rem',
          backgroundColor: '#333', color: '#fff', borderRadius: '6px', fontSize: '0.85rem',
          zIndex: 3000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* Panel Tabs */}
      <div style={styles.tabs}>
        <button style={activeTab === 'entries' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('entries')}>
          📝 Entries
        </button>
        <button style={activeTab === 'review' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('review')}>
          📋 Review Queue
        </button>
        <button style={activeTab === 'analytics' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('analytics')}>
          📊 Analytics
        </button>
      </div>

      {/* ENTRIES TAB */}
      {activeTab === 'entries' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={styles.sectionTitle}>Knowledge Entries</h3>
            <button style={styles.btnPrimary} onClick={handleCreate}>+ New Entry</button>
          </div>

          {/* Status Filter */}
          <div style={{ ...styles.tabs, marginBottom: '0.75rem' }}>
            <button
              style={statusFilter === '' ? styles.tabActive : styles.tab}
              onClick={() => setStatusFilter('')}
            >
              All
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                style={statusFilter === s ? styles.tabActive : styles.tab}
                onClick={() => setStatusFilter(s)}
              >
                {s}
              </button>
            ))}
          </div>

          {loading ? (
            <p style={styles.empty}>Loading...</p>
          ) : entries.length === 0 ? (
            <p style={styles.empty}>No entries found.</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Question</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td style={styles.td}>{entry.question.length > 60 ? entry.question.slice(0, 60) + '...' : entry.question}</td>
                    <td style={styles.td}>{entry.category}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, backgroundColor: statusColors[entry.status] + '20', color: statusColors[entry.status] }}>
                        {entry.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button style={styles.btn} onClick={() => handleEdit(entry)}>✏️</button>
                        <button style={{ ...styles.btn, color: '#d32f2f' }} onClick={() => setDeleteConfirm(entry.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* REVIEW QUEUE TAB */}
      {activeTab === 'review' && (
        <div>
          <h3 style={styles.sectionTitle}>Review Queue</h3>
          {reviewQueue.length === 0 ? (
            <p style={styles.empty}>No entries pending review.</p>
          ) : (
            <div>
              {reviewQueue.map((entry) => (
                <div key={entry.id} style={{ padding: '0.75rem', borderBottom: '1px solid #f0f0f0' }}>
                  <p style={{ margin: '0 0 0.25rem 0', fontWeight: 500, fontSize: '0.9rem' }}>{entry.question}</p>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#555' }}>
                    {entry.answer.length > 150 ? entry.answer.slice(0, 150) + '...' : entry.answer}
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: '#999' }}>
                      Submitted: {new Date(entry.createdAt).toLocaleDateString()}
                    </span>
                    <button style={styles.btnSuccess} onClick={() => handleApprove(entry.id)}>✓ Approve</button>
                    <button style={styles.btnDanger} onClick={() => handleReject(entry.id)}>✕ Reject</button>
                    <button style={styles.btn} onClick={() => handleEdit(entry)}>✏️ Edit</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ANALYTICS TAB */}
      {activeTab === 'analytics' && (
        <div>
          <h3 style={styles.sectionTitle}>Search Analytics</h3>

          {/* Top Queries */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 600, margin: '0 0 0.5rem 0' }}>Top Queries</h4>
            {topQueries.length === 0 ? (
              <p style={styles.empty}>No search data yet.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Query</th>
                    <th style={styles.th}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {topQueries.slice(0, 20).map((q, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{q.queryText}</td>
                      <td style={styles.td}>{q.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Content Gaps */}
          <div style={{ ...styles.sectionDivider, marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 600, margin: '0 0 0.5rem 0' }}>Content Gaps (Zero-Result Queries)</h4>
            {contentGaps.length === 0 ? (
              <p style={styles.empty}>No content gaps detected.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Query</th>
                    <th style={styles.th}>Occurrences</th>
                    <th style={styles.th}>Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {contentGaps.map((g, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{g.queryText}</td>
                      <td style={styles.td}>{g.count}</td>
                      <td style={styles.td}>
                        {g.count > 5 && <span style={{ color: '#d32f2f', fontWeight: 600, fontSize: '0.75rem' }}>⚠️ Needs Content</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Daily Trends */}
          <div style={styles.sectionDivider}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 600, margin: '0 0 0.5rem 0' }}>Daily Search Volume</h4>
            {dailyTrends.length === 0 ? (
              <p style={styles.empty}>No trend data yet.</p>
            ) : (
              <div>
                <div style={styles.barChart}>
                  {dailyTrends.slice(-30).map((t, i) => (
                    <div
                      key={i}
                      style={{
                        ...styles.bar,
                        height: `${(t.count / maxTrendCount) * 100}%`,
                      }}
                      title={`${t.date}: ${t.count} searches`}
                    />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#999', marginTop: '0.25rem' }}>
                  <span>{dailyTrends.length > 0 ? dailyTrends[Math.max(0, dailyTrends.length - 30)].date : ''}</span>
                  <span>{dailyTrends.length > 0 ? dailyTrends[dailyTrends.length - 1].date : ''}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#1a6b3c' }}>
              {editingEntry ? 'Edit Entry' : 'Create Entry'}
            </h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Question</label>
              <input
                style={styles.input}
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Enter the question..."
                maxLength={500}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Answer</label>
              <textarea
                style={styles.textarea}
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                placeholder="Enter the answer..."
                maxLength={5000}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Category</label>
              <select
                style={styles.select}
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Tags (comma-separated)</label>
              <input
                style={styles.input}
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="tag1, tag2, tag3"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Status</label>
              <select
                style={styles.select}
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button style={styles.btn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={styles.modal} onClick={() => setDeleteConfirm(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>Confirm Delete</h3>
            <p style={{ fontSize: '0.85rem', color: '#555' }}>Are you sure you want to delete this entry? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button style={styles.btn} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={styles.btnDanger} onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
